import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Calendar, Clock, Users, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { ProjectWorkforce } from './ProjectWorkforce';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtH = (n) => `${(Math.round((Number(n) || 0) * 10) / 10).toLocaleString('fr-FR')}h`;
const fmtD = (n) => `${(Math.round((Number(n) || 0) * 10) / 10).toLocaleString('fr-FR')}j`;
const MONTH_LABELS_FR = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

const formatMonth = (ym) => {
  if (!ym || ym.length < 7) return ym;
  const y = ym.slice(0, 4);
  const m = parseInt(ym.slice(5, 7), 10);
  return `${MONTH_LABELS_FR[m - 1] || ym} ${y}`;
};

export const ProjectLaborMonthlyTab = ({ project }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const fetchData = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/projects/${project.id}/labor-monthly`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error('Erreur chargement suivi MO');
    } finally {
      setLoading(false);
    }
  }, [project?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;
  if (!data) return null;

  const { totals, rows, day_hours, hourly_rate } = data;
  const fmtEUR = (n) => `${(Math.round((Number(n) || 0))).toLocaleString('fr-FR')} EUR`;

  return (
    <div data-testid="project-labor-monthly-tab" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ProjectWorkforce projectId={project.id} projectName={project.name} />

      <div className="card" style={{ padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Suivi main d'oeuvre mensuel</h3>
            <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: '#5E5F66' }}>Base : {day_hours}h / jour - taux {hourly_rate} EUR/h. Validees uniquement; les heures en attente sont affichees a part.</p>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={fetchData} data-testid="labor-monthly-refresh">
            <ArrowsClockwise size={14} /> Rafraichir
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginTop: 16 }}>
          <div style={{ background: '#F0FDF4', padding: 12, borderRadius: 8, border: '1px solid #BBF7D0' }}>
            <div style={{ fontSize: '0.72rem', color: '#065F46', fontWeight: 600, textTransform: 'uppercase' }}>Total jours valides</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#047857', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Calendar size={18} weight="bold" /> {fmtD(totals.days)}
            </div>
            <div style={{ fontSize: '0.72rem', color: '#5E5F66' }}>{fmtH(totals.hours)}</div>
          </div>
          <div style={{ background: '#EFF6FF', padding: 12, borderRadius: 8, border: '1px solid #BFDBFE' }}>
            <div style={{ fontSize: '0.72rem', color: '#1E3A8A', fontWeight: 600, textTransform: 'uppercase' }}>Cout MO total</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1D4ED8' }}>
              {fmtEUR(totals.cost)}
            </div>
            <div style={{ fontSize: '0.72rem', color: '#5E5F66' }}>en attente : {fmtEUR(totals.cost_pending)}</div>
          </div>
          <div style={{ background: '#FEF3C7', padding: 12, borderRadius: 8, border: '1px solid #FDE68A' }}>
            <div style={{ fontSize: '0.72rem', color: '#92400E', fontWeight: 600, textTransform: 'uppercase' }}>En attente</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#D97706', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Clock size={18} weight="bold" /> {fmtD(totals.days_pending)}
            </div>
            <div style={{ fontSize: '0.72rem', color: '#5E5F66' }}>{fmtH(totals.hours_pending)}</div>
          </div>
          <div style={{ background: '#F8F9FB', padding: 12, borderRadius: 8, border: '1px solid #E2E2E6' }}>
            <div style={{ fontSize: '0.72rem', color: '#374151', fontWeight: 600, textTransform: 'uppercase' }}>Mois suivis</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#374151' }}>{rows.length}</div>
          </div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="empty-state">
          <Clock size={48} className="empty-state-icon" />
          <p className="empty-state-text">Aucune heure pointee sur ce chantier</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="data-table" style={{ margin: 0 }}>
            <thead>
              <tr>
                <th>Mois</th>
                <th style={{ textAlign: 'right' }}>Heures</th>
                <th style={{ textAlign: 'right' }}>Jours</th>
                <th style={{ textAlign: 'right' }}>Cout MO</th>
                <th style={{ textAlign: 'right' }}>En attente</th>
                <th style={{ textAlign: 'center' }}>Salaries</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const isOpen = !!expanded[r.month];
                return (
                  <React.Fragment key={r.month}>
                    <tr
                      data-testid={`labor-monthly-row-${r.month}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setExpanded(p => ({ ...p, [r.month]: !p[r.month] }))}
                    >
                      <td style={{ fontWeight: 600 }}>{formatMonth(r.month)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtH(r.hours)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#047857' }}>{fmtD(r.days)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#1D4ED8' }}>{fmtEUR(r.cost)}</td>
                      <td style={{ textAlign: 'right', color: r.hours_pending > 0 ? '#D97706' : '#9CA3AF' }}>
                        {r.hours_pending > 0 ? `${fmtH(r.hours_pending)} - ${fmtEUR(r.cost_pending)}` : '-'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <Users size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        {r.by_user.length}
                      </td>
                      <td style={{ textAlign: 'right', color: '#9CA3AF', fontSize: '0.72rem' }}>{isOpen ? '▼' : '▶'} Detail</td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={7} style={{ background: '#F8F9FB', padding: 12 }}>
                          <table style={{ width: '100%', fontSize: '0.82rem' }}>
                            <thead>
                              <tr>
                                <th style={{ textAlign: 'left', padding: '4px 8px', color: '#5E5F66' }}>Salarie</th>
                                <th style={{ textAlign: 'right', padding: '4px 8px', color: '#5E5F66' }}>Heures</th>
                                <th style={{ textAlign: 'right', padding: '4px 8px', color: '#5E5F66' }}>Jours</th>
                                <th style={{ textAlign: 'right', padding: '4px 8px', color: '#5E5F66' }}>Cout</th>
                                <th style={{ textAlign: 'right', padding: '4px 8px', color: '#5E5F66' }}>En attente</th>
                              </tr>
                            </thead>
                            <tbody>
                              {r.by_user.map(u => (
                                <tr key={u.user_id}>
                                  <td style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
                                    {u.photo_url ? (
                                      <img src={u.photo_url} alt={u.name} style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }} />
                                    ) : (
                                      <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#E2E2E6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#5E5F66' }}>
                                        {(u.name || '?').slice(0, 1)}
                                      </div>
                                    )}
                                    {u.name}
                                  </td>
                                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{fmtH(u.hours)}</td>
                                  <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600, color: '#047857' }}>{fmtD(u.days)}</td>
                                  <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600, color: '#1D4ED8' }}>{fmtEUR(u.cost)}</td>
                                  <td style={{ padding: '4px 8px', textAlign: 'right', color: u.hours_pending > 0 ? '#D97706' : '#C5C7CD' }}>
                                    {u.hours_pending > 0 ? `${fmtH(u.hours_pending)} / ${fmtEUR(u.cost_pending)}` : '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProjectLaborMonthlyTab;
